import { useEffect, useMemo, useRef, useState } from "react";
import { _ } from "i18n";
import { resolveMediaUrl } from "./mediaUrl.utils";
import { CountrySelection, Gender, TeamGroupClub, TeamGroupTeam } from "./types";
import "./TeamGroupTeams.scss";

const VISIBLE_ENTITY_LIMIT = 10;

export interface TeamGroupVisibleEntities {
	teams: TeamGroupTeam[];
	clubs: TeamGroupClub[];
}

function flattenSelectedCountries(selection: CountrySelection): string[] {
	return Object.values(selection).flat();
}

function EntityAvatar({
	logo,
	name,
	logoBasePath,
}: {
	logo: string | null;
	name: string;
	logoBasePath?: string;
}) {
	const logoSrc = logo !== null && logoBasePath !== undefined ? resolveMediaUrl(logo, logoBasePath) : logo;

	return (
		<span className="team-group-teams__avatar">
			{logoSrc !== null ? (
				<img src={logoSrc} alt="" className="team-group-teams__logo" />
			) : (
				<span className="team-group-teams__initials">{name.slice(0, 2).toUpperCase()}</span>
			)}
		</span>
	);
}

function EntitySection({
	title,
	items,
	logoBasePath,
	entityKeyPrefix,
}: {
	title: string;
	items: Array<{ id: number; logo: string | null; name: string }>;
	logoBasePath?: string;
	entityKeyPrefix: "club" | "team";
}) {
	const [showAll, setShowAll] = useState(false);

	const visibleItems = showAll ? items : items.slice(0, VISIBLE_ENTITY_LIMIT);
	const hasMoreItems = items.length > VISIBLE_ENTITY_LIMIT;

	return (
		<section className="team-group-teams-section">
			<h3 className="team-group-teams-section__title">{title}</h3>
			<div className="team-group-teams">
				{visibleItems.map((item) => (
					<div className="team-group-teams__item" key={`${entityKeyPrefix}-${item.id}`}>
						<EntityAvatar logo={item.logo} name={item.name} logoBasePath={logoBasePath} />
						<span className="team-group-teams__name">{item.name}</span>
					</div>
				))}
			</div>
			{hasMoreItems && (
				<button
					type="button"
					className="team-group-teams-section__show-all"
					onClick={() => setShowAll((expanded) => expanded === false)}
				>
					{showAll ? _("app.player.home.info.collapse") : _("country.selection.network.showAll")}
				</button>
			)}
		</section>
	);
}

export function TeamGroupTeams({
	selected,
	selectedCountries,
	selectedGender,
	logoBasePath,
	fetchVisibleEntities,
}: {
	selected: string[];
	selectedCountries: CountrySelection;
	selectedGender: Gender;
	logoBasePath?: string;
	fetchVisibleEntities: (params: {
		gender: Gender;
		teamGroups: string[];
		countries: string[];
	}) => Promise<TeamGroupVisibleEntities>;
}) {
	const selectedCountryCodes = useMemo(
		() => flattenSelectedCountries(selectedCountries),
		[selectedCountries],
	);
	const fetchParamsKey = useMemo(
		() => `${selectedGender}|${selected.join(",")}|${selectedCountryCodes.join(",")}`,
		[selectedGender, selected, selectedCountryCodes],
	);
	const [visibleEntities, setVisibleEntities] = useState<TeamGroupVisibleEntities>({ teams: [], clubs: [] });
	const requestIdRef = useRef(0);

	useEffect(() => {
		if (selected.length === 0 || selectedCountryCodes.length === 0) {
			setVisibleEntities({ teams: [], clubs: [] });
			return;
		}

		const requestId = ++requestIdRef.current;

		fetchVisibleEntities({
			gender: selectedGender,
			teamGroups: selected,
			countries: selectedCountryCodes,
		})
			.then((entities) => {
				if (requestId === requestIdRef.current) {
					setVisibleEntities(entities);
				}
			})
			.catch(() => undefined);
	}, [fetchVisibleEntities, fetchParamsKey, selected, selectedCountryCodes, selectedGender]);

	if (visibleEntities.teams.length === 0 && visibleEntities.clubs.length === 0) {
		return null;
	}

	const clubsKey = `club-${visibleEntities.clubs.map((club) => club.id).join(",")}`;
	const teamsKey = `team-${visibleEntities.teams.map((team) => team.id).join(",")}`;

	return (
		<div className="team-group-teams-container">
			{visibleEntities.clubs.length > 0 && (
				<EntitySection
					key={clubsKey}
					title={_("country.selection.network.clubsSection")}
					items={visibleEntities.clubs}
					logoBasePath={logoBasePath}
					entityKeyPrefix="club"
				/>
			)}
			{visibleEntities.teams.length > 0 && (
				<EntitySection
					key={teamsKey}
					title={_("country.selection.network.teamsSection")}
					items={visibleEntities.teams}
					logoBasePath={logoBasePath}
					entityKeyPrefix="team"
				/>
			)}
		</div>
	);
}
